import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetQuestionnairesQuery,
  usePostQuestionnaireRequestsMutation,
} from "api/useQuestionnaireQueries";
import { handleQuestionnaireError } from "api/questionnaireHelpers";
import { useSnackbar } from "@mindlercare/ui-web";
import CustomModal from "../../lib/customModal";
import {
  BodyText,
  CheckboxBlack,
  CheckboxRow,
  NewAcceptButton,
  NewStyledQuestionnaireModal,
} from "./modal.styles";

export const SendQuestionnaireModal = ({
  setModalIsOpen,
  patientName,
  patientUserId,
  open,
}: {
  setModalIsOpen: (arg: boolean) => void;
  patientName: string;
  patientUserId: number;
  open: boolean;
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setLoading] = useState(false);
  const { data: questionnaires } = useGetQuestionnairesQuery();

  const { mutateAsync: postQuestionnaireRequests } =
    usePostQuestionnaireRequestsMutation();
  const [checkedQuestionnaires, setCheckedQuestionnaires] = useState(
    new Set<string>(),
  );

  const checkQuestionnaire = (clinicalId: string) => {
    setCheckedQuestionnaires((prev) => {
      const next = new Set(prev);
      if (next.has(clinicalId)) {
        next.delete(clinicalId);
      } else {
        next.add(clinicalId);
      }
      return next;
    });
  };

  const handleSendQuestionnaireRequests = async () => {
    setLoading(true);

    const payload = Array.from(checkedQuestionnaires).map((sQ) => ({
      userId: String(patientUserId),
      clinicalId: sQ,
    }));

    try {
      await postQuestionnaireRequests(payload);
      enqueueSnackbar({
        text: t("QUESTIONNAIRES_SENT", { patientName }),
        isError: false,
      });
    } catch (error) {
      handleQuestionnaireError(
        error,
        "NOT_PATIENT_POST_QUESTIONNAIRES_ERROR",
        enqueueSnackbar,
        t,
      );
    } finally {
      setCheckedQuestionnaires(new Set());
      setLoading(false);
      setModalIsOpen(false);
    }
  };

  return (
    <NewStyledQuestionnaireModal
      onClose={() => setModalIsOpen(false)}
      titleText={`${t("QUESTIONNAIRE")}: ${patientName}`}
      open={open}
      isScrollable
    >
      {questionnaires?.questionnaires?.map((q) => (
        <CheckboxRow
          onClick={() => checkQuestionnaire(q.clinicalId)}
          key={`${q.name}`}
          noPadding
        >
          <div>
            <CheckboxBlack
              key={`cb-${q.name}`}
              onClick={(e) => {
                e.stopPropagation();
                checkQuestionnaire(q.clinicalId);
              }}
              checked={checkedQuestionnaires.has(q.clinicalId)}
              value={checkedQuestionnaires.has(q.clinicalId)}
            />
          </div>
          <div>
            <BodyText
              key={`p-${q.name}`}
            >{`${q.clinicalId}: ${q.clinicalName}`}</BodyText>
          </div>
        </CheckboxRow>
      ))}
      <CustomModal.ButtonRow paddingTop={24}>
        <CustomModal.Button
          variant="secondary"
          onClick={() => {
            setModalIsOpen(false);
          }}
          text={t("CANCEL")}
        ></CustomModal.Button>
        <NewAcceptButton
          variant="primary"
          disabled={checkedQuestionnaires.size === 0}
          onClick={handleSendQuestionnaireRequests}
          data-testid="cy-send-message-button"
          spinning={isLoading}
          text={t("SEND")}
        ></NewAcceptButton>
      </CustomModal.ButtonRow>
    </NewStyledQuestionnaireModal>
  );
};
